import React from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router';
import FlagProvider from '@unleash/proxy-client-react';
import ReactDOM from 'react-dom/client';

import { useUserQuery } from '@api/hooks/useUserQuery';
import { IntlWrapper } from '@components/IntlWrapper';
import { SplashScreen } from '@components/SplashScreen';
import { getUnleashConfig } from '@config/getUnleashConfig';

import { App } from './App';
import { setupStore } from './store';
import { reportWebVitals } from './utils/web-vitals';

import './index.scss';

const prepare = async (): Promise<void> => {
  if (window.config.MSW_ENABLED) {
    // Explicitly delete 'MSW_COOKIE_STORE' key from localStorage to avoid cookies to persist between sessions/logins through MSW Server
    window.localStorage.removeItem('MSW_COOKIE_STORE');

    const { worker } = await import('./mocks/browser');

    await worker.start();
  }
};

const AppGuard = () => {
  const { status } = useUserQuery();

  if (status.isFetching) {
    return <SplashScreen />;
  }

  // If all required resources are ready/fetched, we render the whole app
  return <App />;
};

await prepare();

const store = setupStore();

const unleash = getUnleashConfig({
  appName: window.config.UNLEASH_APP_NAME,
  apiUrl: window.config.UNLEASH_API_URL,
  clientKey: window.config.UNLEASH_CLIENT_KEY,
});

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <IntlWrapper>
      <BrowserRouter>
        <Provider store={store}>
          <FlagProvider config={unleash}>
            <AppGuard />
          </FlagProvider>
        </Provider>
      </BrowserRouter>
    </IntlWrapper>
  </React.StrictMode>
);

// eslint-disable-next-line no-console
reportWebVitals(console.log);
